import localApi from '../../api';
import { IAvailabilityV3BookingsResponse, IBookingsByOwnerIdParams, IBookingsByPropertyIdParams, IBookingsStatisticsByOwnerIdParams } from '../../api/bookings';
import { IBookingsStatistics, IEvent, IEventV2 } from '../../data/entities/event';
import { idOrLegacyId } from '../../entities/properties/selectors';
import { ActionTypeNames, ActionValues, createActionCreators } from '../../store/actions';
import { ThunkAction } from '../../store/middleware';

export const getBookingsByPropertyActions = createActionCreators<
	{ T: 'BOOKINGS_GET_BY_PROP_ID_REQUEST', P: { propertyId: string } },
	{ T: 'BOOKINGS_GET_BY_PROP_ID_RECEIVE', P: { propertyId: string, bookings: IAvailabilityV3BookingsResponse } },
	{ T: 'BOOKINGS_GET_BY_PROP_ID_FAILURE', P: { error: Error } }
>('BOOKINGS_GET_BY_PROP_ID_REQUEST', 'BOOKINGS_GET_BY_PROP_ID_RECEIVE', 'BOOKINGS_GET_BY_PROP_ID_FAILURE');

export const getBookingsByProperty = (id: string | number, params?: IBookingsByPropertyIdParams):
	ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
		const propertyId = idOrLegacyId(id);
		dispatch(getBookingsByPropertyActions.BOOKINGS_GET_BY_PROP_ID_REQUEST({ propertyId }));
		try {
			const bookings = await api.bookings.getBookingSummariesByPropertyId(propertyId, params);
			dispatch(getBookingsByPropertyActions.BOOKINGS_GET_BY_PROP_ID_RECEIVE({ propertyId, bookings }));
		} catch (error) {
			dispatch(getBookingsByPropertyActions.BOOKINGS_GET_BY_PROP_ID_FAILURE({ error }));
			throw error;
		}
	};

export const getFirstBookingIdByOwnerIdActions = createActionCreators<
	{ T: 'FIRST_BOOKING_GET_BY_OWNER_ID_REQUEST', P: { ownerId: string } },
	{ T: 'FIRST_BOOKING_GET_BY_OWNER_ID_RECEIVE', P: { ownerId: string, firstBookingId: string } },
	{ T: 'FIRST_BOOKING_GET_BY_OWNER_ID_FAILURE', P: { error: Error } }
>('FIRST_BOOKING_GET_BY_OWNER_ID_REQUEST', 'FIRST_BOOKING_GET_BY_OWNER_ID_RECEIVE', 'FIRST_BOOKING_GET_BY_OWNER_ID_FAILURE');

export const getFirstBookingIdByOwnerId = (ownerId: string):
	ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
		dispatch(getFirstBookingIdByOwnerIdActions.FIRST_BOOKING_GET_BY_OWNER_ID_REQUEST({ ownerId }));
		try {
			const firstBookingId = await api.bookings.getFirstBookingIdByOwnerId(ownerId);
			dispatch(getFirstBookingIdByOwnerIdActions.FIRST_BOOKING_GET_BY_OWNER_ID_RECEIVE({ ownerId, firstBookingId }));
		} catch (error) {
			dispatch(getFirstBookingIdByOwnerIdActions.FIRST_BOOKING_GET_BY_OWNER_ID_FAILURE({ error }));
			throw error;
		}
	};

export const getBookingByOwnerIdActions = createActionCreators<
	{ T: 'BOOKINGS_GET_BY_OWNER_ID_REQUEST', P: { ownerId: string } },
	{ T: 'BOOKINGS_GET_BY_OWNER_ID_RECEIVE', P: { ownerId: string, bookings: IAvailabilityV3BookingsResponse } },
	{ T: 'BOOKINGS_GET_BY_OWNER_ID_FAILURE', P: { error: Error } }
>('BOOKINGS_GET_BY_OWNER_ID_REQUEST', 'BOOKINGS_GET_BY_OWNER_ID_RECEIVE', 'BOOKINGS_GET_BY_OWNER_ID_FAILURE');

export const getBookingsByOwnerId = (ownerId: string, params?: IBookingsByOwnerIdParams):
	ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
		dispatch(getBookingByOwnerIdActions.BOOKINGS_GET_BY_OWNER_ID_REQUEST({ ownerId }));
		try {
			const bookings = await api.bookings.getBookingsByOwnerId(ownerId, params);
			dispatch(getBookingByOwnerIdActions.BOOKINGS_GET_BY_OWNER_ID_RECEIVE({ ownerId, bookings }));
		} catch (error) {
			dispatch(getBookingByOwnerIdActions.BOOKINGS_GET_BY_OWNER_ID_FAILURE({ error }));
			throw error;
		}
	};

export const getBookingsStatsByOwnerIdActions = createActionCreators<
	{ T: 'BOOKINGS_STATS_GET_BY_OWNER_ID_REQUEST', P: { ownerId: string } },
	{ T: 'BOOKINGS_STATS_GET_BY_OWNER_ID_RECEIVE', P: { ownerId: string, stats: IBookingsStatistics } },
	{ T: 'BOOKINGS_STATS_GET_BY_OWNER_ID_FAILURE', P: { error: Error } }
>('BOOKINGS_STATS_GET_BY_OWNER_ID_REQUEST', 'BOOKINGS_STATS_GET_BY_OWNER_ID_RECEIVE', 'BOOKINGS_STATS_GET_BY_OWNER_ID_FAILURE');

export const getBookingsStatsByOwnerId = (ownerId: string, params?: IBookingsStatisticsByOwnerIdParams):
	ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
		dispatch(getBookingsStatsByOwnerIdActions.BOOKINGS_STATS_GET_BY_OWNER_ID_REQUEST({ ownerId }));
		try {
			const stats = await api.bookings.getBookingsStatsByOwnerId(ownerId, params);
			dispatch(getBookingsStatsByOwnerIdActions.BOOKINGS_STATS_GET_BY_OWNER_ID_RECEIVE({ ownerId, stats }));
		} catch (error) {
			dispatch(getBookingsStatsByOwnerIdActions.BOOKINGS_STATS_GET_BY_OWNER_ID_FAILURE({ error }));
			throw error;
		}
	};

export const getBookingByIdActions = createActionCreators<
	{ T: 'BOOKINGS_GET_BY_ID_REQUEST', P: { propertyId: string, id: string } },
	{ T: 'BOOKINGS_GET_BY_ID_RECEIVE', P: IEventV2 },
	{ T: 'BOOKINGS_GET_BY_ID_FAILURE', P: { error: Error } }
>('BOOKINGS_GET_BY_ID_REQUEST', 'BOOKINGS_GET_BY_ID_RECEIVE', 'BOOKINGS_GET_BY_ID_FAILURE');

export const getBookingById = (id: string | number, eventId: string): ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(id);
	dispatch(getBookingByIdActions.BOOKINGS_GET_BY_ID_REQUEST({ propertyId, id: eventId }));
	try {
		const theBooking = await api.bookings.getBookingById(propertyId, eventId);
		dispatch(getBookingByIdActions.BOOKINGS_GET_BY_ID_RECEIVE(theBooking));
	} catch (error) {
		dispatch(getBookingByIdActions.BOOKINGS_GET_BY_ID_FAILURE({ error }));
		throw error;
	}
};

export const getBookingByEventIdActions = createActionCreators<
	{ T: 'BOOKINGS_GET_BY_EVENT_ID_REQUEST', P: { id: string } },
	{ T: 'BOOKINGS_GET_BY_EVENT_ID_RECEIVE', P: IEvent },
	{ T: 'BOOKINGS_GET_BY_EVENT_ID_FAILURE', P: { error: Error } }
>('BOOKINGS_GET_BY_EVENT_ID_REQUEST', 'BOOKINGS_GET_BY_EVENT_ID_RECEIVE', 'BOOKINGS_GET_BY_EVENT_ID_FAILURE');

export const getBookingByEventId = (id: string): ThunkAction<any> => async (dispatch) => {
	dispatch(getBookingByEventIdActions.BOOKINGS_GET_BY_EVENT_ID_REQUEST({ id }));
	try {
		const theBooking = await localApi.availability.getBookingByEventId(id);
		dispatch(getBookingByEventIdActions.BOOKINGS_GET_BY_EVENT_ID_RECEIVE(theBooking));
	} catch (error) {
		dispatch(getBookingByEventIdActions.BOOKINGS_GET_BY_EVENT_ID_FAILURE({ error }));
	}
};

export const getBookingsBySearchTextActions = createActionCreators<
	{ T: 'BOOKINGS_GET_BY_SEARCH_TEXT_REQUEST', P: { text: string } },
	{ T: 'BOOKINGS_GET_BY_SEARCH_TEXT_RECEIVE', P: IEvent[] },
	{ T: 'BOOKINGS_GET_BY_SEARCH_TEXT_FAILURE', P: { error: Error } }
>('BOOKINGS_GET_BY_SEARCH_TEXT_REQUEST', 'BOOKINGS_GET_BY_SEARCH_TEXT_RECEIVE', 'BOOKINGS_GET_BY_SEARCH_TEXT_FAILURE');

export const getBookingsBySearchText = (text: string): ThunkAction<any> => async (dispatch) => {
	dispatch(getBookingsBySearchTextActions.BOOKINGS_GET_BY_SEARCH_TEXT_REQUEST({ text }));
	try {
		const bookings = await localApi.availabilityV3.getBookingsBySearchText(text);
		dispatch(getBookingsBySearchTextActions.BOOKINGS_GET_BY_SEARCH_TEXT_RECEIVE(bookings));
	} catch (error) {
		dispatch(getBookingsBySearchTextActions.BOOKINGS_GET_BY_SEARCH_TEXT_FAILURE({ error }));
	}
};

export const cancelBookingActions = createActionCreators<
	{ T: 'BOOKINGV2_CANCEL_REQUEST', P: { propertyId: string, eventId: string } },
	{ T: 'BOOKINGV2_CANCEL_RECEIVE', P: { eventId: string, propertyId: string, events: IEventV2[] } },
	{ T: 'BOOKINGV2_CANCEL_FAILURE', P: { eventId: string, error: Error } }
>('BOOKINGV2_CANCEL_REQUEST', 'BOOKINGV2_CANCEL_RECEIVE', 'BOOKINGV2_CANCEL_FAILURE');

export const cancelBooking = (id: string | number, eventId: IEventV2['id']): ThunkAction =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		const propertyId = idOrLegacyId(id);
		dispatch(cancelBookingActions.BOOKINGV2_CANCEL_REQUEST({ propertyId, eventId }));
		try {
			const event = await api.bookings.cancelBooking(propertyId, eventId) as IEventV2;
			dispatch(cancelBookingActions.BOOKINGV2_CANCEL_RECEIVE({ eventId, propertyId, events: [event] }));
		} catch (error) {
			dispatch(cancelBookingActions.BOOKINGV2_CANCEL_FAILURE({ eventId, error }));
			throw error;
		}
	};

export const updateBookingActions = createActionCreators<
	{ T: 'BOOKINGV2_UPDATE_REQUEST', P: { propertyId: string, eventId: string } },
	{ T: 'BOOKINGV2_UPDATE_RECEIVE', P: { propertyId: string, eventId: string, event: IEventV2 } },
	{ T: 'BOOKINGV2_UPDATE_FAILURE', P: { eventId: string, error: Error } }
>('BOOKINGV2_UPDATE_REQUEST', 'BOOKINGV2_UPDATE_RECEIVE', 'BOOKINGV2_UPDATE_FAILURE');

export const updateBooking = (propertyId: IEventV2['property_id'], eventId: IEventV2['id'], event: IEventV2): ThunkAction =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		dispatch(updateBookingActions.BOOKINGV2_UPDATE_REQUEST({ propertyId, eventId }));
		try {
			const propId = idOrLegacyId(propertyId);
			const returnedEvent = await api.bookings.updateBooking(propId, eventId, event) as IEventV2;
			dispatch(updateBookingActions.BOOKINGV2_UPDATE_RECEIVE({ eventId, propertyId: event.property_id, event: returnedEvent }));
		} catch (error) {
			dispatch(updateBookingActions.BOOKINGV2_UPDATE_FAILURE({ eventId, error }));
			throw error;
		}
	};

export const getRUBookingCurrencyActions = createActionCreators<
	{ T: 'BOOKING_RENTALSUNITED_CURRENCY_GET_REQUEST', P: { propertyId: string, eventId: string, rentalsunitedBookingId: string | number } },
	{
		T: 'BOOKING_RENTALSUNITED_CURRENCY_GET_RECEIVE', P: {
			propertyId: string,
			eventId: string,
			rentalsunitedBookingId: string | number,
			event: IEventV2
		}
	},
	{
		T: 'BOOKING_RENTALSUNITED_CURRENCY_GET_FAILURE', P: {
			propertyId: string,
			eventId: string,
			rentalsunitedBookingId: string | number,
			error: Error
		}
	}
>('BOOKING_RENTALSUNITED_CURRENCY_GET_REQUEST', 'BOOKING_RENTALSUNITED_CURRENCY_GET_RECEIVE', 'BOOKING_RENTALSUNITED_CURRENCY_GET_FAILURE');

export const getRUBookingCurrency = (
	id: IEventV2['property_id'],
	eventId: IEventV2['id'],
	rentalsunitedBookingId: string | number,
	currency?: string
): ThunkAction =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		const propertyId = idOrLegacyId(id);
		dispatch(getRUBookingCurrencyActions.BOOKING_RENTALSUNITED_CURRENCY_GET_REQUEST({ propertyId, eventId, rentalsunitedBookingId }));
		try {
			const response = await api.rentalsunitedCurrency.getRentalsunitedCurrency(rentalsunitedBookingId, currency);
			const event = await dispatch(getBookingById(propertyId, eventId));
			dispatch(getRUBookingCurrencyActions.BOOKING_RENTALSUNITED_CURRENCY_GET_RECEIVE({ propertyId, eventId, rentalsunitedBookingId, event }));

			return response;
		} catch (error) {
			dispatch(getRUBookingCurrencyActions.BOOKING_RENTALSUNITED_CURRENCY_GET_FAILURE({ propertyId, eventId, rentalsunitedBookingId, error }));
			throw error;
		}
	};

export const requestBookingPaymentActions = createActionCreators<
	{ T: 'BOOKING_PAYMENT_REQUEST', P: { propertyId: string, eventId: string } },
	{ T: 'BOOKING_PAYMENT_RECEIVE', P: { propertyId: string, eventId: string } },
	{ T: 'BOOKING_PAYMENT_FAILURE', P: { propertyId: string, eventId: string, error: Error } }
>('BOOKING_PAYMENT_REQUEST', 'BOOKING_PAYMENT_RECEIVE', 'BOOKING_PAYMENT_FAILURE');

export const requestBookingPayment = (id: IEventV2['property_id'], eventId: IEventV2['id']): ThunkAction =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		const propertyId = idOrLegacyId(id);
		dispatch(requestBookingPaymentActions.BOOKING_PAYMENT_REQUEST({ propertyId, eventId }));
		try {
			await api.bookingPayment.requestBookingPayment(propertyId, eventId);
			dispatch(requestBookingPaymentActions.BOOKING_PAYMENT_RECEIVE({ propertyId, eventId }));
		} catch (error) {
			dispatch(requestBookingPaymentActions.BOOKING_PAYMENT_FAILURE({ propertyId, eventId, error }));
			throw error;
		}
	};

const actions = {
	...getBookingsByPropertyActions,
	...getBookingByIdActions,
	...getBookingsBySearchTextActions,
	...getBookingByEventIdActions,
	...getBookingByOwnerIdActions,
	...getFirstBookingIdByOwnerIdActions,
	...getBookingsStatsByOwnerIdActions,
	...cancelBookingActions,
	...updateBookingActions,
	...getRUBookingCurrencyActions,
	...requestBookingPaymentActions
};

type BookingActions = ActionValues<typeof actions>;
type BookingActionTypes = ActionTypeNames<BookingActions>;
export type Actions = BookingActions[keyof BookingActions];
export const ActionTypes = Object.keys(actions).reduce((types, t) => {
	types[t] = t;
	return types;
}, {} as BookingActionTypes);
